import {Box, Divider} from "@mui/material";
import {useTranslation} from "react-i18next";
import {FolioFormat} from "./folioFormat";
import {FolioTotals} from "./folioLine.util";
import {folioArrivalSx} from "./folioStyles";
import {LineArrivals} from "./useLineArrivals";

interface Props {
    totals: FolioTotals
    format: FolioFormat
    /** A folio that can't change shows its stored total: that is what was charged. */
    storedTotalCents?: number
    /** Lines that arrived from the server: the totals the last of them changes light up with it. */
    arrivals?: LineArrivals
    /** The tip the guest signed, until Confirm and charge posts it: a faded Tip row, and in the total. */
    pendingTipCents?: number
}

interface RowProps {
    label: string
    value: string
    strong?: boolean
    /** Changed by the last arrival (counted): its value lights up - again with each arrival. */
    lit?: number
    /** Not on the folio yet: faded, the hint on hover. */
    pendingHint?: string
}

// The lines' type: 14px like the app's tables, the total larger
const Row = ({label, value, strong, lit, pendingHint}: RowProps) => (
    <>
        <Box
            component={'span'}
            title={pendingHint}
            sx={{
                fontSize: strong ? 16 : 14,
                fontWeight: strong ? 600 : 400,
                color: strong ? 'text.primary' : 'text.secondary',
                lineHeight: '20px',
                opacity: pendingHint ? 0.5 : undefined,
            }}
        >
            {label}
        </Box>
        <Box
            component={'span'}
            key={lit ? `lit-${lit}` : 'value'}
            data-arrived={lit ? true : undefined}
            data-pending-tip={pendingHint ? true : undefined}
            sx={[
                {
                    fontSize: strong ? 16 : 14,
                    fontWeight: strong ? 600 : 400,
                    lineHeight: '20px',
                    textAlign: 'right',
                    fontVariantNumeric: 'tabular-nums',
                    whiteSpace: 'nowrap',
                    opacity: pendingHint ? 0.5 : undefined,
                },
                // The tint around the figures, without moving them
                !!lit && (theme => ({...folioArrivalSx(theme), px: 0.5, mx: -0.5, borderRadius: '4px'})),
            ]}
        >
            {value}
        </Box>
    </>
);

/**
 * Subtotal, the VAT included and total - all in the folio's currency. When a line has a discount
 * (its ⋮ menu, the chip in its price), the subtotal is the one before them and the discounts follow it,
 * so the rows still add up to the total. The VAT and the tip, like the discounts, have a row only when
 * there is one: an amount of 0 never shows (exclusive VAT, a folio without VAT).
 * What a line arriving from the server changes lights up: the tip Confirm and charge posts brings its
 * row lit. Until then the tip the guest signed is a faded row of its own, and the total includes it -
 * what the guest signed, what Confirm and charge charges; the VAT stays (a tip has none).
 */
const FolioSummary = ({totals, format, storedTotalCents, arrivals, pendingTipCents = 0}: Props) => {
    const {t} = useTranslation();
    const discounted = totals.discountCents > 0;
    const tipped = totals.tipCents > 0;
    const last = arrivals?.count || undefined;
    const charges = arrivals?.charges ? last : undefined;

    return (
        <Box
            role={'group'}
            aria-label={t('Summary')}
            sx={{display: 'grid', gridTemplateColumns: 'auto auto', columnGap: 4, rowGap: 0.5, alignItems: 'baseline', minWidth: 220}}
        >
            <Row label={t('Subtotal')} value={format.money(totals.subtotalCents + (discounted ? totals.discountCents : 0))} lit={charges}/>
            {discounted && <Row label={t('Discounts')} value={`−${format.money(totals.discountCents)}`} lit={charges}/>}
            {totals.vatCents !== 0 && <Row label={t('Included VAT')} value={format.money(totals.vatCents)} lit={charges}/>}
            {tipped && <Row label={t('Tip')} value={format.money(totals.tipCents)} lit={arrivals?.tip ? last : undefined}/>}
            {pendingTipCents > 0 && <Row label={t('Tip')} value={format.money(pendingTipCents)} pendingHint={t('Added when you confirm')}/>}
            <Divider sx={{gridColumn: '1 / -1', my: 1}}/>
            <Row label={t('Total')} value={format.money((storedTotalCents ?? totals.totalCents) + pendingTipCents)} strong lit={last}/>
        </Box>
    );
};

export default FolioSummary;
